import { useEffect, useState, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe, StripeElementLocale } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Button, TextGrayDarkSemiboldRegular } from '@companio/companio_ui';

import {
  AddPaymentMethodFormWrapper
} from '@/pages/AddCard/components/AddPaymentMethodForm/AddPaymentMethodForm.styled';
import { BILLING_SETTINGS_PAGE_PATH } from '@/constants/routes';
import { PAYMENT_METHODS_QUERY_KEY } from '@/constants/react_query_keys';
import { usePageTitleContext } from '@/contexts/PageTitleContext';
import { useGetPaymentClientSecret } from '@/hooks/queries/useGetPaymentClientSecret';
import { useGetCurrentUser } from '@/hooks/queries/useGetCurrentUser';
import { useVerifyStripePayment } from '@/hooks/mutations/useVerifyStripePayment';
import { centsToEuroDisplaSymbolAfter } from '@/utils/currency';
import { isValidNumber } from '@/utils/helpers';
import { PaymentSkelleton } from './PaymentComponents';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

export default function PaymentPage() {
  const { t } = useTranslation();
  const { setPageTitle } = usePageTitleContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const paymentId = searchParams.get('paymentId') || '';

  const paymentClientSecretQuery = useGetPaymentClientSecret({ paymentId });
  const currentUserQuery = useGetCurrentUser();

  const locale = currentUserQuery.data?.user.locale.slice(0, 1) || 'en';
  const clientSecret = paymentClientSecretQuery.data?.client_secret;
  const serviceDescription = paymentClientSecretQuery.data?.payment?.description;
  const amount = isValidNumber(paymentClientSecretQuery.data?.payment?.amount)
    ? centsToEuroDisplaSymbolAfter(Number(paymentClientSecretQuery.data?.payment?.amount))
    : '';

  const {
    baseAmount = 0,
    penaltyDays = 0,
    penaltyAmount = 0
  } = paymentClientSecretQuery.data?.payment?.metadata || {};

  useEffect(() => {
    setPageTitle({
      content: t('page_title_add_card'),
      link: BILLING_SETTINGS_PAGE_PATH
    });
  }, [setPageTitle, t]);

  useEffect(() => {
    if (paymentClientSecretQuery.isError) {
      navigate(BILLING_SETTINGS_PAGE_PATH);
    }
  }, [paymentClientSecretQuery.isError, navigate]);

  if (paymentClientSecretQuery.isLoading) {
    return <PaymentSkelleton />;
  }

  return (
    <AddPaymentMethodFormWrapper>
      <h2 className="title">{serviceDescription}</h2>

      {!baseAmount && !penaltyDays && !penaltyAmount ? null : (
        <div className="other-payment-amounts">
          {baseAmount ? (
            <TextGrayDarkSemiboldRegular>
              {t('base_amount', { baseAmount: centsToEuroDisplaSymbolAfter(Number(baseAmount)) })}
            </TextGrayDarkSemiboldRegular>
          ) : null}

          {penaltyDays ? (
            <TextGrayDarkSemiboldRegular>
              {t('penalty_days', { penaltyDays })}
            </TextGrayDarkSemiboldRegular>
          ) : null}

          {penaltyAmount ? (
            <TextGrayDarkSemiboldRegular>
              {t('penalty_amount', {
                penaltyAmount: centsToEuroDisplaSymbolAfter(Number(penaltyAmount))
              })}
            </TextGrayDarkSemiboldRegular>
          ) : null}
        </div>
      )}
      <TextGrayDarkSemiboldRegular style={{ marginBottom: '2rem' }}>
        {t('full_amount_to_pay', { amount })}
      </TextGrayDarkSemiboldRegular>

      {paymentClientSecretQuery.isSuccess && clientSecret && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: clientSecret,
            locale: locale as StripeElementLocale,
            appearance: {
              theme: 'stripe',
              variables: { colorPrimary: '#0d6efd' }
            }
          }}>
          <PaymentStripeForm clientSecret={clientSecret} paymentId={paymentId} />
        </Elements>
      )}
    </AddPaymentMethodFormWrapper>
  );
}

function PaymentStripeForm({
  clientSecret,
  paymentId
}: {
  clientSecret: string;
  paymentId: string;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const verifyStripePaymentMutation = useVerifyStripePayment();

  async function handleSubmit(event: SyntheticEvent) {
    event.preventDefault();
    setIsSubmitting(true);

    if (!stripe || !elements) {
      setIsSubmitting(false);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required'
    });

    if (error) {
      toast.error(error.message);
      setIsSubmitting(false);
    } else {
      verifyPayment();
    }
  }

  function verifyPayment() {
    const secretSplit = clientSecret.split('_secret_');
    const paymentIntentId = secretSplit[0];

    verifyStripePaymentMutation.mutate(
      {
        paymentId: paymentId,
        paymentIntentId: paymentIntentId
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: [PAYMENT_METHODS_QUERY_KEY] });
          toast.success(t('payment_verified_success'));
          navigate(BILLING_SETTINGS_PAGE_PATH);
        },
        onError: (err: any) => {
          toast.error(err.message || t('error_verifying_payment'));
          setIsSubmitting(false);
        }
      }
    );
  }

  return (
    <>
      <PaymentElement />
      <div className="buttons">
        <Button
          as={Link}
          to={BILLING_SETTINGS_PAGE_PATH}
          $variant="gray"
          type="button"
          disabled={isSubmitting || verifyStripePaymentMutation.isPending}>
          {t('cancel')}
        </Button>
        <Button
          $variant="primary"
          type="submit"
          onClick={handleSubmit}
          disabled={isSubmitting || verifyStripePaymentMutation.isPending}>
          {isSubmitting ? t('processing') : t('confirm')}
        </Button>
      </div>
    </>
  );
}
